import { supabase } from "@/platform/lib/supabase";
import { readErrorFromResponse } from "@/platform/billing/errors";

export type DiscursiveDiscipline = "biologia" | "quimica" | "fisica" | "matematica" | "ingles";

export interface DiscursiveQuestionRow {
  id: string;
  number: number | null;
  stem: string;
  topic: string | null;
  exam_year: number;
}

export interface GradeDiscursivePayload {
  discipline: DiscursiveDiscipline;
  question_id?: string;
  stem: string;
  reference_answer?: string;
  student_answer?: string;
  image_paths?: string[];
}

export interface GradeDiscursiveResult {
  transcription?: string;
  scores: Record<string, number>;
  total: number;
  feedback: string;
  minimal_answer: string;
  excellent_answer: string;
  confidence: number;
  attempt_id: string | null;
  source: "text" | "image";
}

export async function listDiscursiveQuestions(
  discipline: DiscursiveDiscipline,
  limit = 20,
): Promise<DiscursiveQuestionRow[]> {
  const { data, error } = await supabase
    .from("einstein_questions")
    .select("id, number, stem, topic, einstein_exam_papers!inner(year)")
    .eq("kind", "discursive")
    .eq("discipline", discipline)
    .order("number", { ascending: true })
    .limit(limit);
  if (error) throw error;
  type Raw = {
    id: string;
    number: number | null;
    stem: string;
    topic: string | null;
    einstein_exam_papers: { year: number };
  };
  return (data as unknown as Raw[]).map((r) => ({
    id: r.id,
    number: r.number,
    stem: r.stem,
    topic: r.topic,
    exam_year: r.einstein_exam_papers.year,
  }));
}

export async function uploadDiscursiveImages(params: {
  userId: string;
  submissionUuid: string;
  files: File[];
}): Promise<string[]> {
  const paths: string[] = [];
  for (let i = 0; i < params.files.length; i++) {
    const file = params.files[i];
    if (!file) continue;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${params.userId}/discursive-${params.submissionUuid}/page-${i + 1}.${ext}`;
    const { error } = await supabase.storage
      .from("einstein-essays")
      .upload(path, file, { contentType: file.type, upsert: true });
    if (error) throw new Error(`upload ${path}: ${error.message}`);
    paths.push(path);
  }
  return paths;
}

export async function gradeDiscursive(payload: GradeDiscursivePayload): Promise<GradeDiscursiveResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Sessão expirada. Entre novamente.");
  const res = await fetch("/api/grade-discursive", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw await readErrorFromResponse(res);
  }
  return (await res.json()) as GradeDiscursiveResult;
}
